import { Injectable, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { catchError, of } from 'rxjs';
import { AuthService } from './auth.service';
import { RouteTrackerService } from './route-tracker.service';
import { TrackingLoaderService } from './tracking-loader.service';
import {environment} from 'src/environments/environment';

/**
 * The app's single analytics entry point.
 *
 * Every call is gated on the user's analytics-cookie consent. Gating here rather than at each call
 * site means a new `logEvent` added anywhere is covered by default; the failure mode of forgetting
 * is "no event", not "an unconsented event".
 *
 * `COLLECTION_ENABLED: false` in `AppModule` covers what the Firebase SDK emits on its own
 * (automatic screen views and user ids); this covers what the app emits deliberately.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly tracking = inject(TrackingLoaderService);

  /**
   * The signed-in user, or null/undefined until Firebase has restored the session.
   *
   * Errors collapse to null rather than propagating: a failure to read auth state must cost
   * attribution, never the app.
   */
  private readonly authUser = toSignal(
    inject(AuthService).getUser().pipe(catchError(() => of(null)))
  );

  /**
   * Whether auth state has answered, so a caller that wants its event attributed knows the uid is
   * as good as it is going to get. Three states collapse to two here: no answer yet is not
   * resolved, while an answer of "nobody is signed in" is — otherwise an event would wait forever
   * on a visitor who never signs in.
   */
  public readonly attributionResolved = computed(() => this.authUser() !== undefined);

  constructor(private analytics: AngularFireAnalytics,
              private routeTracker: RouteTrackerService) {
    // gtag keeps `user_id` in per-page-load configuration, so attribution has to be established on
    // every load; `getUser()` emits the restored session on each one. Both inputs are read here so
    // that either arrival order attributes the session: the banner is gated on being signed in, so
    // a first-ever sign-in has the uid before there is any consent to send it under, while a
    // returning visitor's stored grant is applied before Firebase finishes restoring.
    effect(() => {
      const user = this.authUser();
      if (user === undefined || !this.tracking.analyticsAllowed()) return;
      // Null clears the id, which is what a sign-out needs: on a shared device the next person's
      // events must not carry the last person's. The compat v8 types narrow the parameter to
      // `string`, but the SDK documents null as the way to unset it.
      this.analytics.setUserId(user?.uid ?? null);
    });
  }

  logEvent(name: string, options: any = null){
    if (!this.tracking.analyticsAllowed()) return;
    let storeId = this.routeTracker.getStoreIdSnapshot();
    let finalOptions = options? {...options } : {};
    if (storeId) {
      finalOptions['store_id'] = storeId;
    }
    if (environment.clientId) {
      finalOptions['client_id'] = environment.clientId;
    }
    this.analytics.logEvent(name, finalOptions);
  }
}
